// Nav rows for the direction B rail. Presentational: callers pass translated labels and decide what is
// active, open or locked, so the dashboard tree and the reviewer area's short list share the same rows.
//
//   <SidebarNavList label tourAnchor?>   <nav aria-label> + <ul>; `tourAnchor` → data-tour on the list
//   <SidebarNavItem>                     a link row (or, with `locked`, a button that calls onLockedClick)
//     to, label, icon, collapsed        collapsed: icon only, label in a tooltip on the right
//     active?                           aria-current="page" + active styling
//     badge?                            translated badge text (a dot when collapsed)
//     locked?, lockedLabel?, onLockedClick?   dimmed row with a lock; a button that opens a dialog, no navigation
//     onNavigate?                       runs on click (haptics, closing the mobile drawer)
//     tourAnchor?                       data-tour on the <li>
//   <SidebarNavGroup>                    a disclosure row with sub-links; collapsed: a flyout menu
//     label, icon, collapsed, active?, badge?, open, onOpenChange, items, onNavigate?, tourAnchor?
//     items: { to, label, icon, active?, tourAnchor? }[]
import { ChevronRight, Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { useId } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/lumen/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/lumen/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/lumen/tooltip';
import { cn } from '@/lib/utils';

const rowClasses =
  'group/row relative flex min-h-8 w-full items-center gap-2.5 rounded-control px-2.5 py-1.5 text-left font-medium text-13 text-fg-2 outline-none transition-colors duration-150 hover:bg-tint hover:text-fg focus-visible:ring-2 focus-visible:ring-label focus-visible:ring-inset';
const activeRowClasses = 'bg-surface font-semibold text-fg hover:bg-surface';
const collapsedRowClasses = 'justify-center px-0';
const iconClasses = 'flex size-4 shrink-0 items-center justify-center text-fg-muted group-hover/row:text-fg-2';
const activeIconClasses = 'text-label group-hover/row:text-label';
const subRowClasses =
  'flex min-h-7 w-full items-center rounded-control px-2.5 py-1 text-13 text-fg-2 outline-none transition-colors duration-150 hover:bg-tint hover:text-fg focus-visible:ring-2 focus-visible:ring-label focus-visible:ring-inset';
const activeSubRowClasses = 'bg-surface-2 font-semibold text-fg hover:bg-surface-2';

export function SidebarNavList({
  label,
  tourAnchor,
  children,
}: {
  /** Accessible name of the <nav>. */
  label: string;
  tourAnchor?: string;
  children: ReactNode;
}) {
  return (
    <nav aria-label={label}>
      <ul className="flex flex-col gap-px" data-tour={tourAnchor}>
        {children}
      </ul>
    </nav>
  );
}

function RowBadge({ badge, collapsed }: { badge?: string; collapsed: boolean }) {
  if (!badge) return null;
  if (collapsed) {
    return <span aria-hidden className="absolute top-1 right-1.5 size-1.5 rounded-full bg-label" />;
  }
  return (
    <Badge variant="info" className="ml-auto">
      {badge}
    </Badge>
  );
}

/** Wraps a collapsed row in a tooltip on the right that shows its label. */
function RailTooltip({ label, enabled, children }: { label: string; enabled: boolean; children: ReactNode }) {
  if (!enabled) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

export interface SidebarNavItemProps {
  label: string;
  icon: ReactNode;
  collapsed: boolean;
  /** Target route; unused when `locked`. */
  to?: string;
  active?: boolean;
  badge?: string;
  locked?: boolean;
  /** Screen-reader text for the lock (e.g. "Module not active"). */
  lockedLabel?: string;
  onLockedClick?: () => void;
  onNavigate?: () => void;
  tourAnchor?: string;
}

export function SidebarNavItem({
  label,
  icon,
  collapsed,
  to,
  active = false,
  badge,
  locked = false,
  lockedLabel,
  onLockedClick,
  onNavigate,
  tourAnchor,
}: SidebarNavItemProps) {
  const content = (
    <>
      <span aria-hidden className={cn(iconClasses, active && activeIconClasses)}>
        {icon}
      </span>
      {collapsed ? <span className="sr-only">{label}</span> : <span className="min-w-0 flex-1 truncate">{label}</span>}
      {locked ? (
        <>
          {!collapsed && <Lock aria-hidden className="size-3 shrink-0 text-fg-muted" />}
          {lockedLabel && <span className="sr-only">{`, ${lockedLabel}`}</span>}
        </>
      ) : (
        <RowBadge badge={badge} collapsed={collapsed} />
      )}
    </>
  );

  if (locked) {
    return (
      <li data-tour={tourAnchor}>
        <RailTooltip label={label} enabled={collapsed}>
          <button
            type="button"
            aria-haspopup="dialog"
            onClick={onLockedClick}
            className={cn(rowClasses, 'text-fg-muted', collapsed && collapsedRowClasses)}
          >
            {content}
          </button>
        </RailTooltip>
      </li>
    );
  }

  return (
    <li data-tour={tourAnchor}>
      <RailTooltip label={label} enabled={collapsed}>
        <Link
          to={to ?? '#'}
          onClick={onNavigate}
          aria-current={active ? 'page' : undefined}
          className={cn(rowClasses, active && activeRowClasses, collapsed && collapsedRowClasses)}
        >
          {content}
        </Link>
      </RailTooltip>
    </li>
  );
}

export interface SidebarNavSubItem {
  to: string;
  label: string;
  icon: ReactNode;
  active?: boolean;
  tourAnchor?: string;
}

export interface SidebarNavGroupProps {
  label: string;
  icon: ReactNode;
  collapsed: boolean;
  active?: boolean;
  badge?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: SidebarNavSubItem[];
  onNavigate?: () => void;
  tourAnchor?: string;
}

export function SidebarNavGroup({
  label,
  icon,
  collapsed,
  active = false,
  badge,
  open,
  onOpenChange,
  items,
  onNavigate,
  tourAnchor,
}: SidebarNavGroupProps) {
  const listId = useId();
  const iconSlot = (
    <span aria-hidden className={cn(iconClasses, active && activeIconClasses)}>
      {icon}
    </span>
  );

  // Collapsed: the group opens as a menu to the right of the rail (click, Enter, Space or ArrowDown),
  // with the group's name as its heading, so every sub-link stays one step away.
  if (collapsed) {
    return (
      <li data-tour={tourAnchor}>
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn(rowClasses, collapsedRowClasses, active && activeRowClasses)}
                  aria-label={label}
                >
                  {iconSlot}
                  <RowBadge badge={badge} collapsed />
                </button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8}>
              {label}
            </TooltipContent>
          </Tooltip>
          <DropdownMenuContent side="right" align="start" sideOffset={8} className="min-w-52">
            <DropdownMenuLabel className="text-fg-muted text-xs">{label}</DropdownMenuLabel>
            {items.map((item) => (
              <DropdownMenuItem
                key={item.to}
                asChild
                className={cn('text-13', item.active && 'bg-surface-2 font-semibold')}
              >
                <Link to={item.to} onClick={onNavigate} aria-current={item.active ? 'page' : undefined}>
                  <span aria-hidden className="flex size-4 items-center justify-center text-fg-muted">
                    {item.icon}
                  </span>
                  {item.label}
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </li>
    );
  }

  // Expanded: a disclosure button. The sub-list is mounted only while open, so a running tour sees a
  // sub-link anchor only when it can be shown (docs/onboarding.md: absent anchors drop their step).
  return (
    <li data-tour={tourAnchor}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => onOpenChange(!open)}
        className={cn(rowClasses, active && activeRowClasses)}
      >
        {iconSlot}
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <RowBadge badge={badge} collapsed={false} />
        <ChevronRight
          aria-hidden
          className={cn('size-3.5 shrink-0 text-fg-muted transition-transform duration-150', open && 'rotate-90')}
        />
      </button>
      {open && (
        <ul id={listId} className="flex flex-col gap-px pt-0.5 pb-1.5 pl-6.5">
          {items.map((item) => (
            <li key={item.to} data-tour={item.tourAnchor}>
              <Link
                to={item.to}
                onClick={onNavigate}
                aria-current={item.active ? 'page' : undefined}
                className={cn(subRowClasses, item.active && activeSubRowClasses)}
              >
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
